import type { Translation } from './types';
import { hideArrayProperties } from './types';

export const de: Translation = hideArrayProperties<Translation>({
  meta: {
    title: 'Aletiq — Deine KI baut. Aletiq prüft.',
    description:
      'Dein KI-Agent baut deine Webseite und prüft sie automatisch mit Aletiq. Vier-Augen-Prinzip für KI-generierte Web-Apps.',
    ogTitle: 'Aletiq — Deine KI baut. Aletiq prüft.',
    ogDescription: 'Vier-Augen-Prinzip für KI-generierte Web-Apps. Automatisch, sicher, ohne Aufwand.',
  },
  nav: {
    home: 'Start',
    howItWorks: 'So funktioniert\u2019s',
    useCases: 'Anwendungsfälle',
    security: 'Sicherheit',
    pricing: 'Preise',
    contact: 'Kontakt',
    blog: 'Blog',
    cta: 'Kostenlos starten',
    languageSwitch: 'EN',
  },

  // ── Hero ────────────────────────────────────────────────────────────────────
  hero: {
    badge: 'Für Claude Code, Cursor & Codex via MCP',
    headlineLine1: 'Deine KI baut —',
    headlineLine2: 'Aletiq prüft',
    subheadline:
      'Dein KI-Agent produziert Code schneller als du prüfen kannst. Aletiq prüft automatisch, gibt Feedback an den Agent, und der korrigiert sofort — du siehst nur das fertige Ergebnis.',
    ctaPrimary: 'Early Access anfragen',
    ctaSecondary: 'So funktioniert\u2019s',
    chatFlow: [
      { role: 'user', text: 'Bau mir einen Online-Shop mit Checkout' },
      { role: 'agent', text: 'Baue Produktseite, Warenkorb und Checkout...' },
      { role: 'agent', text: 'aletiq --verify "teste den Checkout" --page /checkout' },
      { role: 'aletiq', text: 'Testdaten vorbereitet: Produkt + Adresse eingefügt', status: 'pass' },
      { role: 'aletiq', text: 'Checkout-Button reagiert nicht auf Mobile', status: 'fail' },
      { role: 'agent', text: 'Fehler erkannt — korrigiere Touch-Target...' },
      { role: 'aletiq', text: 'Re-Run: Checkout komplett durchlaufen', status: 'pass' },
    ],
    demoLines: [
      { type: 'user', text: 'Bau mir einen Online-Shop mit Checkout' },
      { type: 'blank', text: '' },
      { type: 'agent', text: 'Ich baue Produktseite, Warenkorb und Checkout.' },
      { type: 'blank', text: '' },
      { type: 'tool-call', text: 'aletiq --verify "teste den Checkout" --page /checkout' },
      { type: 'blank', text: '' },
      { type: 'pass', text: 'Testdaten vorbereitet: Produkt + Adresse eingefügt' },
      { type: 'pass', text: 'Navigation: alle Links funktionieren' },
      { type: 'fail', text: 'Checkout-Button reagiert nicht auf Mobile' },
      { type: 'blank', text: '' },
      { type: 'agent', text: 'Fehler erkannt — korrigiere Touch-Target...' },
      { type: 'blank', text: '' },
      { type: 'tool-call', text: 'aletiq --verify "re-run" (kostenlos)' },
      { type: 'blank', text: '' },
      { type: 'pass', text: 'Re-Run: Checkout komplett durchlaufen' },
      { type: 'pass', text: '3/3 Prüfungen bestanden' },
    ],
  },

  // ── Works With ──────────────────────────────────────────────────────────────
  worksWith: {
    subtitle: 'Integration',
    heading: 'Am besten mit',
    headingHighlight: 'Claude Code',
    description: 'Aletiq nutzt MCP — den offenen Standard für Tool-Integration. Claude Code hat Hooks, die Aletiq nach jeder Änderung automatisch aufrufen. Cursor und Codex funktionieren über Rules.',
    agents: [
      { name: 'Claude Code', logo: '/images/agents/claude-code.svg' },
      { name: 'Cursor', logo: '/images/agents/cursor.svg' },
      { name: 'Codex', logo: '/images/agents/codex.svg' },
      { name: 'Windsurf', logo: '/images/agents/windsurf.svg' },
      { name: 'Cline', logo: '/images/agents/cline.svg' },
      { name: 'Continue', logo: '/images/agents/continue.svg' },
    ],
  },

  // ── How It Works (Homepage short version) ───────────────────────────────────
  howItWorks: {
    label: 'So einfach geht\u2019s',
    heading: 'So einfach',
    headingHighlight: 'funktioniert\u2019s',
    intro: 'Du beschreibst, was du willst. Dein KI-Agent baut es, prüft es mit Aletiq — und du siehst das Ergebnis.',
    steps: [
      {
        number: '01',
        title: 'Du beschreibst',
        description: 'Sag deinem KI-Agent, was du brauchst. Ganz normal, in deinen Worten.',
      },
      {
        number: '02',
        title: 'Dein Agent baut & prüft',
        description: 'Der Agent baut dein Feature, ruft Aletiq auf, bekommt Feedback — und korrigiert Fehler sofort selbst.',
      },
      {
        number: '03',
        title: 'Du siehst das Ergebnis',
        description: 'Aletiq gibt deinem Agent ein klares Ergebnis zurück: bestanden, Achtung, oder Fehler — direkt in deinem Workflow.',
      },
    ],
    linkText: 'Mehr erfahren',
    linkButton: 'Details ansehen',
  },

  // ── Why Aletiq (replaces Verification Levels) ──────────────────────────────
  whyAletiq: {
    label: 'Warum Aletiq?',
    heading: 'Warum Teams',
    headingHighlight: 'auf Aletiq setzen',
    cards: [
      {
        icon: '💻',
        title: 'Läuft auf deinem Rechner',
        description: 'Der Aletiq Runner testet direkt auf localhost — zero Latency, kein Deploy nötig. Dein Agent bekommt in Sekunden Feedback statt Minuten.',
      },
      {
        icon: '🔄',
        title: 'Geschlossener Kreislauf',
        description: 'Aletiq findet einen Fehler, dein Agent korrigiert ihn sofort — ohne dass du eingreifen musst. Sogar Testdaten werden automatisch vorbereitet.',
      },
      {
        icon: '🔒',
        title: 'Deine Daten bleiben lokal',
        description: 'Kein Cloud-Server sieht deinen Code. Der Runner läuft bei dir — das macht Aletiq günstig für uns und sicher für dich.',
      },
      {
        icon: '🚪',
        title: 'Es kann nicht schummeln',
        description: 'Dein Coding-Agent hat vollen Zugriff — hängt er an einem kaputten Button, kann er an der UI vorbeigreifen und einen grünen Test vortäuschen. Aletiq sieht nur, was deine Nutzer sehen: den Browser. Ist etwas kaputt, kann Aletiq nicht so tun, als wäre es heil.',
      },
      {
        icon: '🎯',
        title: 'Klare Ergebnisse',
        description: 'Dein Agent ruft aletiq --verify auf und bekommt ein eindeutiges Ergebnis: bestanden oder Fehler — mit konkreten Details zum Fix.',
      },
    ],
  },

  // ── Use Cases (Homepage cards) ──────────────────────────────────────────────
  useCases: {
    label: 'Anwendungsfälle',
    heading: 'Deine KI macht',
    headingHighlight: 'ständig Dinge kaputt',
    description: 'Dein Coding-Agent baut schnell — aber er driftet, überschreibt und zerstört was gestern noch funktioniert hat. Aletiq fängt das auf, bevor du es merkst.',
    viewAllButton: 'Alle Szenarien ansehen',
    cards: [
      {
        slug: 'regression',
        icon: '🔄',
        title: 'Der Agent hat den Warenkorb gefixt — und den Login kaputt gemacht',
        description: 'Jede KI-Änderung kann etwas anderes zerstören. Aletiq führt nach jeder Änderung alle bisherigen Tests erneut aus — kostenlos.',
      },
      {
        slug: 'drift',
        icon: '🎯',
        title: 'Der Agent hat etwas anderes gebaut als du wolltest',
        description: 'Du hast "Kontaktformular" gesagt. Der Agent hat ein Newsletter-Signup gebaut. Aletiq prüft, ob das Gebaute deiner Absicht entspricht.',
      },
      {
        slug: 'silent-fail',
        icon: '👻',
        title: 'Es sieht gut aus — aber der Button tut nichts',
        description: 'Die Seite rendert perfekt. Aber der Checkout-Button ist tot. Kein Fehler, kein Crash — einfach kaputt. Aletiq klickt jeden Button und prüft ob er funktioniert.',
      },
      {
        slug: 'overnight',
        icon: '🌙',
        title: 'Dein Agent hat die ganze Nacht gecodet — funktioniert davon was?',
        description: 'Du wachst auf und hast 15 neue Features. Aletiq hat jedes einzelne geprüft während es gebaut wurde. Du siehst die Ergebnisse, nicht die Bugs.',
      },
    ],
  },

  // ── CTA ─────────────────────────────────────────────────────────────────────
  cta: {
    headline: 'Bereit?',
    headlineHighlight: 'Starte kostenlos',
    button: 'Kostenlos starten →',
    card: {
      title: 'Kostenlos loslegen',
      description: 'Unbegrenzte Prüfungen. Unbegrenzte Wiederholungen. Keine Kreditkarte nötig.',
      link: 'Alle Pläne ansehen →',
    },
  },

  // ── Proof Bar ───────────────────────────────────────────────────────────────
  proofBar: {
    badges: [
      { icon: '🔁', text: 'Re-Runs immer gratis' },
      { icon: '🧠', text: 'Muscle Memory Effekt' },
      { icon: '🤖', text: 'Null manueller Aufwand' },
      { icon: '💰', text: 'Bis zu 91% günstiger' },
    ],
  },

  // ── Muscle Memory ──────────────────────────────────────────────────────────
  muscleMemory: {
    label: 'Muscle Memory',
    heading: 'Einmal testen,',
    headingHighlight: 'für immer geschützt',
    description:
      'Aletiq merkt sich jeden Test. Jeder neue Build wird automatisch gegen alle bisherigen Tests geprüft — kostenlos. Dein Sicherheitsnetz wächst mit jedem Feature, ohne dass du etwas tun musst.',
    steps: [
      { number: 'Woche 1', label: 'Kontaktformular gebaut', checks: '4 Tests' },
      { number: 'Woche 2', label: '+ Checkout hinzugefügt', checks: '11 Tests' },
      { number: 'Woche 4', label: '+ Blog & Dashboard', checks: '31 Tests' },
      { number: 'Woche 8', label: '+ Payment & Login', checks: '47 Re-Runs gratis' },
    ],
    caption: 'Neue Tests kosten Tokens. Alle 47 Wiederholungen: $0.',
  },

  // ── Token Savings ──────────────────────────────────────────────────────────
  tokenSavings: {
    label: 'Kosten & Effizienz',
    heading: '',
    headingHighlight: '91% günstiger als manuelles Testen',
    description:
      'Dein AI-Agent produziert Code schneller als du reviewen kannst. Ohne Aletiq musst du alles manuell prüfen — oder Fehler in Production finden. Mit Aletiq: automatisch, sofort, bis zu 91% günstiger.',
    comparison: [
      { label: 'Manuelles Testen', tokens: 'Du prüfst jeden Build selbst', bar: 30 },
      { label: 'Ohne Verification', tokens: 'Bugs in Production → teurer Fix', bar: 15 },
      { label: 'Mit Aletiq', tokens: 'Automatisch, Re-Runs gratis', bar: 100, highlight: true },
    ],
    caption: 'Lokal auf deinem Rechner + Mixed-Model Strategie (Re-Runs mit schnellem Modell) = bis zu 91% günstiger.',
  },

  // ── Sticky CTA ─────────────────────────────────────────────────────────────
  stickyCta: {
    text: 'Re-Runs gratis',
    button: 'Kostenlos starten',
  },

  // ── Pricing ─────────────────────────────────────────────────────────────────
  pricing: {
    label: 'Preise',
    title: 'Einfach. Transparent. Fair.',
    popularBadge: 'Beliebtester Plan',
    allPlansLabel: 'In allen Plänen enthalten',
    tiers: [
      {
        name: 'Free',
        price: '$0',
        period: 'für immer',
        verifications: 'Unbegrenzte Prüfungen',
        features: [
          'Unbegrenzte Prüfungen & Wiederholungen',
          'Ein Projekt',
          'Einzelner Judge',
          'Bridge (Agent stellt Fragen)',
          'CI- & API-Zugang',
        ],
        cta: 'Kostenlos starten',
      },
      {
        name: 'Pro',
        price: '$39',
        period: '/Monat',
        verifications: 'Unbegrenzte Prüfungen',
        features: [
          'Alles aus Free',
          '3-Judge-Jury (Multi-Provider-Konsensus)',
          'Bis zu 10 Projekte',
          'Priority-Support',
        ],
        cta: 'Pro starten',
        popular: true,
      },
      {
        name: 'Premium',
        price: '$89',
        period: '/Monat',
        verifications: 'Unbegrenzte Prüfungen',
        features: [
          'Alles aus Pro',
          'Drift-Erkennung (baut dein Agent das Richtige?)',
          'Varianten-Generierung (aus 1 Test werden 10)',
          'Unbegrenzte Projekte',
          'Priority-Queue',
        ],
        cta: 'Premium starten',
      },
    ],
    allPlansInclude: [
      'Unbegrenzte Prüfungen & Wiederholungen',
      'Lokaler Runner auf deinem Rechner',
      'MCP-Integration (Claude Code, Cursor, Codex)',
      'Bridge — dein Agent fragt Aletiq wenn er nicht weiterkommt',
    ],
    faq: {
      label: 'FAQ',
      title: 'Häufig gestellte Fragen',
      items: [
        {
          question: 'Sind Prüfungen wirklich unbegrenzt?',
          answer: 'Ja. Jeder Plan bietet unbegrenzte Prüfungen und Wiederholungen. Das können wir, weil der Aletiq Runner auf deinem Rechner läuft — wir zahlen nur für das KI-Verdict, was uns Bruchteile eines Cents kostet.',
        },
        {
          question: 'Was ist die 3-Judge-Jury?',
          answer: 'Free nutzt ein KI-Modell für das Verdict. Pro nutzt drei verschiedene KI-Modelle (OpenAI, Google, DeepSeek) und nimmt eine Mehrheitsentscheidung — deutlich zuverlässigere Ergebnisse.',
        },
        {
          question: 'Was ist Drift-Erkennung?',
          answer: 'Premium-Feature: Aletiq prüft, ob das was dein Agent baut noch zu deiner ursprünglichen Absicht passt. Fängt Scope-Drift auf, bevor er zum Problem wird.',
        },
        {
          question: 'Was ist Varianten-Generierung?',
          answer: 'Premium-Feature: Aletiq generiert automatisch Edge-Case-Varianten deiner Tests — andere Rollen, falsche Eingaben, Mobile-Viewports — Dinge die dein Agent alleine nie testen würde.',
        },
        {
          question: 'Welche KI-Agents funktionieren mit Aletiq?',
          answer: 'Claude Code (beste Integration via MCP + Hooks), Cursor (via .cursorrules), Codex (via AGENTS.md) und jeder MCP-kompatible Agent.',
        },
        {
          question: 'Brauche ich eine Kreditkarte für Free?',
          answer: 'Nein. Der Free-Plan ist dauerhaft kostenlos, ohne Kreditkarte.',
        },
      ],
    },
  },

  // ── How It Works (detail page) ──────────────────────────────────────────────
  howItWorksPage: {
    title: 'So funktioniert Aletiq',
    subtitle: 'Dein KI-Agent baut. Aletiq prüft automatisch. Du siehst das Ergebnis.',
    heroLabel: 'So funktioniert\u2019s',
    heroTitle: 'Dein Agent baut —',
    heroHighlight: 'Aletiq prüft',
    steps: [
      {
        number: '01',
        title: 'Du beschreibst, was du willst',
        description: 'Du sprichst mit deinem KI-Agent — nicht mit Aletiq direkt. Aletiq arbeitet im Hintergrund, unsichtbar für dich.',
        details: [
          '"Bau mir einen Online-Shop mit Warenkorb"',
          '"Füge ein Kontaktformular hinzu"',
          '"Erstelle eine Landing Page für mein Startup"',
        ],
      },
      {
        number: '02',
        title: 'Dein Agent baut — und prüft automatisch',
        description: 'Der Agent hat Aletiq als Tool eingebaut. Nach jedem Feature ruft er aletiq --verify auf. Aletiq testet, bereitet Testdaten vor und gibt Feedback — der Agent korrigiert sofort.',
        details: [
          'Geschlossener Kreislauf: prüfen → Feedback → korrigieren → erneut prüfen',
          'Testdaten werden automatisch vorbereitet (Formulare, Logins, Bestellungen)',
          'Du musst nicht eingreifen — Agent und Aletiq regeln das untereinander',
        ],
      },
      {
        number: '03',
        title: 'Du siehst klare Ergebnisse',
        description: 'Bestanden, Achtung oder Fehler — mit konkreten Details was geprüft wurde. Dein Agent kann sofort reagieren.',
        details: [
          'Bestanden: Alles funktioniert wie gewünscht',
          'Achtung: Etwas sieht verdächtig aus (z.B. langsame Ladezeit)',
          'Fehler: Etwas funktioniert nicht — Aletiq sagt deinem Agent genau was',
        ],
      },
    ],
    resultLabels: {
      pass: 'Bestanden',
      warn: 'Achtung',
      fail: 'Fehler',
    },
  },

  // ── Use Cases (detail page) ─────────────────────────────────────────────────
  useCasesPage: {
    title: 'Was Aletiq für dich prüft',
    subtitle: 'Egal was dein KI-Agent baut — Aletiq prüft, ob es funktioniert.',
    heroLabel: 'Anwendungsfälle',
    heroTitle: 'Egal was dein Agent baut \u2014',
    heroHighlight: 'Aletiq pr\u00fcft es',
    terminalLabel: 'Live Beispiel',
    terminalTitle: 'So sieht eine Pr\u00fcfung aus',
    terminalDescription: 'Dein Agent ruft aletiq --verify auf. Aletiq bereitet Testdaten vor, pr\u00fcft alles und gibt konkretes Feedback.',
    scenariosLabel: 'Szenarien',
    scenariosTitle: 'Was dein Agent baut \u2014',
    scenariosHighlight: 'Aletiq pr\u00fcft es',
    cards: [
      {
        slug: 'online-shop',
        icon: '🛒',
        title: 'Online-Shop',
        whatYouSay: 'Bau mir einen Shop mit Warenkorb',
        whatAletiqChecks: 'Können Kunden Produkte hinzufügen und bezahlen?',
      },
      {
        slug: 'landing-page',
        icon: '🚀',
        title: 'Landing Page',
        whatYouSay: 'Erstelle eine Landing Page für mein Startup',
        whatAletiqChecks: 'Funktionieren alle Links? Sieht es auf Mobile gut aus?',
      },
      {
        slug: 'buchungssystem',
        icon: '📅',
        title: 'Buchungssystem',
        whatYouSay: 'Ich brauche ein Terminbuchungs-Tool',
        whatAletiqChecks: 'Können Nutzer Termine auswählen und bestätigen?',
      },
      {
        slug: 'dashboard',
        icon: '📊',
        title: 'Dashboard',
        whatYouSay: 'Bau mir ein Kunden-Dashboard',
        whatAletiqChecks: 'Laden die Daten? Funktioniert die Navigation?',
      },
      {
        slug: 'blog',
        icon: '📝',
        title: 'Blog',
        whatYouSay: 'Erstelle einen Blog mit Kommentarfunktion',
        whatAletiqChecks: 'Können Besucher Beiträge lesen und kommentieren?',
      },
      {
        slug: 'mitgliederbereich',
        icon: '🔐',
        title: 'Mitgliederbereich',
        whatYouSay: 'Bau einen Login-Bereich für meine Kunden',
        whatAletiqChecks: 'Funktioniert Registrierung, Login und Passwort vergessen?',
      },
    ],
  },

  // ── Security Page (replaces Architecture) ───────────────────────────────────
  securityPage: {
    title: 'Sicherheit & Datenschutz',
    subtitle: 'Deine Daten bleiben bei dir. Immer.',
    heroLabel: 'Sicherheit',
    heroTitle: 'Deine Daten bleiben',
    heroHighlight: 'bei dir. Immer.',
    architectureLabel: 'Architektur',
    architectureTitle: 'Alles läuft auf deinem Rechner',
    architectureDescription: 'Der Aletiq Runner läuft lokal auf deinem Rechner. Dein Code, deine Daten und deine Tests verlassen nie dein Netzwerk. Nur anonymisierte Nutzungsstatistiken erreichen unsere Server.',
    points: [
      {
        icon: '🏠',
        title: 'Deine Daten bleiben bei dir',
        description: 'Alle Tests laufen auf deinem Rechner. Dein Code und deine Daten verlassen nie dein Netzwerk.',
      },
      {
        icon: '👁️',
        title: 'Wir sehen deinen Code nicht',
        description: 'Nur Testergebnisse und Beobachtungen werden analysiert. Dein Quellcode bleibt privat.',
      },
      {
        icon: '🤖',
        title: 'Dein Agent, deine Kontrolle',
        description: 'Aletiq tut nur, was dein Agent fragt. Keine eigenständigen Aktionen, keine versteckten Zugriffe.',
      },
      {
        icon: '🔒',
        title: 'Verschlüsselte Kommunikation',
        description: 'Alle Verbindungen sind verschlüsselt. Deine Daten sind unterwegs genauso sicher wie bei dir.',
      },
    ],
  },

  // ── Contact ─────────────────────────────────────────────────────────────────
  contact: {
    title: 'Kontakt',
    subtitle: 'Schreib uns — wir melden uns schnell',
    nameLabel: 'Name',
    emailLabel: 'E-Mail',
    companyLabel: 'Unternehmen',
    messageLabel: 'Nachricht',
    submitButton: 'Nachricht senden',
    salesHeading: 'Fragen zu Premium oder Team?',
    validation: {
      nameMin: 'Name muss mindestens 2 Zeichen lang sein',
      emailInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein',
      messageMin: 'Nachricht muss mindestens 10 Zeichen lang sein',
    },
    status: {
      success: 'Deine Nachricht wurde gesendet. Wir melden uns in Kürze!',
      error: 'Etwas ist schiefgelaufen. Bitte versuch es später nochmal.',
    },
  },
  earlyAccess: {
    title: 'Early Access anfragen',
    subtitle: 'Aletiq ist im Early Access. Wir onboarden Design Partner manuell, damit das Produkt zu deinem Build-Workflow passt.',
    badge: 'Early Access',
    intro: 'Sag uns, was du baust und wie du es heute verifizierst. Wenn es passt, melden wir uns innerhalb von 2 Werktagen mit einem privaten Setup.',
    trustBullets: [
      { icon: '•', text: 'Keine Kreditkarte nötig' },
      { icon: '•', text: 'Begrenzte Plätze — wir onboarden Design Partner' },
      { icon: '•', text: 'Antwort innerhalb von 2 Werktagen' },
    ],
    form: {
      nameLabel: 'Dein Name',
      emailLabel: 'Arbeits-E-Mail',
      emailHint: 'Bitte Arbeits-E-Mail — hilft uns bei der Priorisierung.',
      companyLabel: 'Firma',
      stackLabel: 'Stack / Framework',
      stackPlaceholder: 'z.B. Next.js + Vercel, SvelteKit, Rails',
      goalLabel: 'Was willst du verifizieren?',
      goalPlaceholder: 'Kurz: welche Flows baut dein KI-Agent, und wo scheitert er aktuell? (1–2 Sätze)',
      githubLabel: 'GitHub-Handle (optional)',
      githubHint: 'Optional — hilft uns, deine Arbeit besser einzuschätzen.',
      submitButton: 'Zugang anfragen',
      submittingButton: 'Wird gesendet…',
    },
    validation: {
      nameMin: 'Bitte gib deinen Namen ein',
      emailInvalid: 'Bitte gib eine gültige Arbeits-E-Mail ein',
      stackMin: 'Bitte nenne deinen Stack',
      goalMin: 'Bitte beschreibe, was du verifizieren willst (mindestens 10 Zeichen)',
    },
    status: {
      successTitle: 'Angekommen — danke!',
      successDetail: 'Wir prüfen deine Anfrage und melden uns innerhalb von 2 Werktagen. Check dein Postfach (auch Spam) für die Bestätigung.',
      error: 'Etwas ist schiefgelaufen. Bitte versuch es nochmal oder schreib uns an hello@aletiq.ai.',
    },
  },
  thankYou: {
    title: 'Danke — Nachricht erhalten.',
    subtitle: 'Wir melden uns innerhalb von 1–2 Werktagen. Schau auch in deinen Spam-Ordner.',
    backHome: 'Zurück zur Startseite',
  },
  notFound: {
    title: 'Seite nicht gefunden',
    description: 'Die Seite, die du suchst, existiert nicht oder wurde verschoben.',
    backHome: 'Zurück zur Startseite',
  },
  footer: {
    copyright: '© 2026 Aletiq. Alle Rechte vorbehalten.',
    builtBy: 'Ein Produkt von Denqfabrik',
    privacy: 'Datenschutz',
    terms: 'AGB',
    imprint: 'Impressum',
  },
  structuredData: {
    description:
      'Aletiq prüft automatisch, ob KI-generierte Web-Apps funktionieren. Vier-Augen-Prinzip für KI-Agenten — sicher, schnell, ohne Aufwand.',
    offerDescriptions: [
      'Automatische Qualitätsprüfung für KI-generierte Webseiten',
      'Vier-Augen-Prinzip: Dein Agent baut, Aletiq prüft',
      'Echte Browser-Tests mit klaren Ergebnissen für deinen Agent',
    ],
  },
});
